import { Button } from "../../../../components/ui/button";
import { DialogContent, DialogHeader } from "../../../../components/ui/dialog";
import { useLocation, useParams } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from "../../../../components/ui/form";
import StarIcon from "../../../../assets/svgs/common/star.svg";
import usePostStore from "../../../../store/post.store";
import { handleHttpError, useHandleNavigate } from "../../../../utils/utils";
import { useToast } from "../../../../hooks/use-toast";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import { usePostsRejectDraftEmphemeralHttp } from "../../../../hooks/http/ephemeral/campaigns/posts-reject-draft.emphemeral.http";
import { useRejectPostEphemeralHttp } from "../../../../hooks/http/ephemeral/posts/reject-post.ephemeral.http";

const FormSchema = z.object({
	reasonKey: z.string().optional(),
	message: z.string().min(5, "Please provide a reason for rejection"),
});

const DraftRejectReasons: Record<string, string> = {
	DOESNT_FOLLOW_BRIEF:
		"Your draft doesn't match the campaign brief. Please review the brief carefully and resubmit.",
	POOR_CONTENT_QUALITY:
		"This content doesn't meet our quality standards. Please improve (lighting, clarity, framing) and resubmit.",
	DUPLICATE_CONTENT:
		"This content appears to be copied or reused. Please submit original content for this campaign.",
	DUPLICATE_FROM_ANOTHER_PHASE:
		"This content was already submitted in a previous phase. Please create fresh content for this phase and resubmit.",
	WRONG_PRODUCT_OR_FORMAT:
		"The content doesn't feature the right product or format. Please refer to the brief and resubmit.",
};

const PostRejectReasons: Record<string, string> = {
	POST_LINK_UNAVAILABLE:
		"Your post link is unavailable or broken. Please check that the post is public and resubmit the correct link.",
	POST_WAS_DELETED:
		"Your post appears to have been deleted. Please repost the approved content and submit the new link.",
	WRONG_CONTENT_POSTED:
		"The content in this link doesn't match the approved draft. Please post the correct content and resubmit.",
	MISSING_REQUIRED_TAGS:
		"Your post is missing required hashtags or mentions as per the brief. Please update the post and resubmit the link.",
	MISSING_REQUIRED_CAPTION_TEXT:
		"Your caption is missing required text from the brief. Please update the caption and resubmit the link.",
};

export const PostRejectModalBrandPartial: React.FunctionComponent = () => {
	const { postId } = useParams();

	const { fetching: fetchingRejectDraft, execute: rejectDraft } =
		usePostsRejectDraftEmphemeralHttp();
	const { fetching: fetchingRejectPost, execute: rejectPost } =
		useRejectPostEphemeralHttp();

	const location = useLocation();
	const { removeById } = usePostStore();
	const { toast } = useToast();

	function handleReturnPath() {
		if (location.pathname.includes("draft-reject")) {
			return location.pathname.replace(`/${postId}/draft-reject`, "");
		}
		if (location.pathname.includes("post-reject")) {
			return location.pathname.replace(`/${postId}/post-reject`, "");
		}
		return location.pathname;
	}

	const { handleNavigate } = useHandleNavigate(handleReturnPath());
	const isDraftReject = location.pathname.endsWith("draft-reject");
	const isPostReject = location.pathname.endsWith("post-reject");
	const activeReasons = isDraftReject
		? DraftRejectReasons
		: isPostReject
			? PostRejectReasons
			: {};

	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			reasonKey: undefined,
			message: "",
		},
	});

	function handleReasonChange(reasonKey: string) {
		form.setValue("reasonKey", reasonKey, { shouldDirty: true });
		const template = activeReasons[reasonKey];
		if (template) {
			form.setValue("message", template, {
				shouldDirty: true,
				shouldValidate: true,
			});
		}
	}

	async function onSubmit(data: z.infer<typeof FormSchema>) {
		if (!postId) return;

		let response;
		if (location.pathname.includes("draft-reject")) {
			response = await rejectDraft({
				body: { message: data.message },
				pathParams: { id: postId },
			});
		} else if (location.pathname.includes("post-reject")) {
			response = await rejectPost({
				body: { message: data.message },
				pathParams: { id: postId },
			});
		}

		if (!response) return;

		if (!response.success) {
			return handleHttpError(response, toast);
		}

		removeById(postId);
		handleNavigate();
	}

	function ModalType() {
		if (location.pathname.endsWith("draft-reject")) {
			return {
				title: "Reason for rejecting draft",
				description: "Give a brief for the rejection of the draft",
				placeholder: "describe the reason for the draft rejection here...",
				buttonText: "Reject draft",
			};
		}
		if (location.pathname.endsWith("post-reject")) {
			return {
				title: "Reason for rejecting post",
				description: "Give a brief for the rejection of the post",
				placeholder: "describe the reason for the post rejection here...",
				buttonText: "Reject post",
			};
		}
	}

	return (
		<DialogContent className="flex flex-col items-center gap-5 border-border rounded-xl w-96 md:w-full text-center text-primary">
			<DialogHeader className="w-full">
				<div className="flex flex-col items-start">
					<p className="flex gap-1 font-semibold text-xl md:text-28 leading">
						{ModalType()?.title} <StarIcon />
					</p>
					<span className="text-secondary">{ModalType()?.description}</span>
				</div>
			</DialogHeader>
			<div className="border-b border-border w-full" />
			<div className="flex items-center gap-4 w-full">
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="relative flex flex-col space-y-8 w-full"
					>
						{Object.keys(activeReasons).length > 0 && (
							<div className="flex flex-col gap-4 text-left">
								<p className="flex items-center gap-1 font-semibold text-base">
					{isDraftReject
						? "Choose a reason for rejecting draft"
						: isPostReject
							? "Choose a reason for rejecting post"
													: "Choose a reason for rejecting post"} <StarIcon />
								</p>
								<div className="gap-4 grid grid-cols-1 md:grid-cols-2">
									{Object.keys(activeReasons).map((key) => (
										<div key={key} className="flex items-center gap-2">
											<Input
												type="radio"
												id={key}
												checked={form.watch("reasonKey") === key}
												className="w-5 h-5 accent-primarybuttonbg"
												value={key}
												onChange={() => handleReasonChange(key)}
											/>
											<Label
												htmlFor={key}
												className="font-medium text-sm leading-snug cursor-pointer"
											>
								{(() => {
									switch (key) {
										case "DOESNT_FOLLOW_BRIEF":
											return "Doesn't follow the brief";
										case "POOR_CONTENT_QUALITY":
											return "Poor content quality";
										case "DUPLICATE_CONTENT":
											return "Copied / duplicate content";
										case "DUPLICATE_FROM_ANOTHER_PHASE":
											return "Duplicate from another phase";
										case "WRONG_PRODUCT_OR_FORMAT":
											return "Wrong product or format";
										case "POST_LINK_UNAVAILABLE":
											return "Post link unavailable";
										case "POST_WAS_DELETED":
											return "Post was deleted";
										case "WRONG_CONTENT_POSTED":
											return "Wrong content posted";
										case "MISSING_REQUIRED_TAGS":
											return "Missing required tags / mentions";
										case "MISSING_REQUIRED_CAPTION_TEXT":
											return "Missing required caption text";
										default:
											return key;
									}
								})()}
											</Label>
										</div>
									))}
								</div>
							</div>
						)}

						<FormField
							control={form.control}
							name="message"
							render={({ field }) => (
								<FormItem className="text-start">
									<FormControl>
										<Textarea
											className="px-6 pt-5 border border-border rounded-xl h-48 font-semibold placeholder:text-secondary resize-none"
											placeholder={ModalType()?.placeholder || ""}
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<Button
							type="submit"
							disabled={fetchingRejectDraft || fetchingRejectPost}
							className="bg-rejectbuttonbg py-6 border border-rejectbuttonborder w-fit font-semibold text-rejectbuttontext tracking-wider self-end"
						>
							{ModalType()?.buttonText}
						</Button>
					</form>
				</Form>
			</div>
		</DialogContent>
	);
};
